import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {
  Table,
  Button,
  message,
  Space,
  Input,
  Select, Row, Typography, Col,
} from "antd";
import {useApiGet, useApiPost} from "../../hooks/useApi";
import { toSnakeCase } from "./csvUtils";
import CSVImportButton from "./widgets/CSVImportButton";
import useFillHeight from "../../hooks/useFillHeight";
import { VIEW } from "../../utils/constants";

const {Title, Text} = Typography;

// Normalisation pour comparer les libellés sans être sensible à la casse,
// aux espaces superflus/multiples, ni aux accents
const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // retire les accents
    .replace(/\s+/g, " ") // espaces multiples -> un seul espace
    .trim()
    .toLowerCase();

// Normalisation "clé" plus stricte pour le matching : retire TOUTE la
// ponctuation et les espaces, en plus des accents et de la casse.
// Ex : "Lot n°12" -> "lot12", "L'Orée" -> "loree", "T2 - Bât A" -> "t2bata"
const normalizeKey = (value) =>
  normalize(value).replace(/[^a-z0-9]+/g, "");

// Cherche le produit correspondant à une valeur "Logement" du CSV :
// 1) correspondance exacte (clé normalisée) sur designation ou reference
// 2) à défaut, correspondance partielle (l'une contient l'autre)
const findMatchingProduct = (logementValue, products) => {
  const value = normalizeKey(logementValue);
  if (!value) return undefined;

  // 1) correspondance exacte sur designation OU reference
  const exactMatch = products.find(
    (p) =>
      normalizeKey(p.designation) === value ||
      normalizeKey(p.reference) === value
  );
  if (exactMatch) return exactMatch;

  // 2) correspondance partielle, en ignorant les valeurs vides
  //    (sinon includes("") renverrait toujours true et matcherait n'importe quoi)
  return products.find((p) => {
    const designation = normalizeKey(p.designation);
    const reference = normalizeKey(p.reference);

    const partial = (candidate) =>
      candidate !== "" &&
      (candidate.includes(value) || value.includes(candidate));

    return partial(designation) || partial(reference);
  });
};

function ImportComponent() {
  const {execute: apiGet, abort, data, loading: loadingQuery} = useApiGet('products-simple');
  const {execute: apiGetImport, data: dataImport, loading: loadingQueryImport} = useApiGet('imports');
  const {execute: apiPost, loading: loadingPost} = useApiPost('import-data');

  // Métadonnées des colonnes (indépendantes du rendu, pour pouvoir
  // reconstruire dynamiquement les en-têtes)
  const [baseColumns, setBaseColumns] = useState([]); // [{ dataIndex, key, headerLabel }]
  const [dataSource, setDataSource] = useState([]);

  // Lot sélectionné pour chaque ligne du tableau final : { [rowKey]: productId }
  const [lotSelection, setLotSelection] = useState({});

  // Colonnes cochées / incluses pour l'export : { [dataIndex]: boolean }
  // Toutes les colonnes sont cochées par défaut à chaque nouvel import.
  const [columnSelection, setColumnSelection] = useState({});

  // État pour la recherche textuelle sur le tableau final
  const [searchText, setSearchText] = useState("");

  // Option "Générer contrat" pilotée par la société de l'utilisateur connecté
  const generateContract = useSelector(
    (state) =>
      state.utilisateur.data?.utilisateur_societe?.generate_contract
  );

  // Configuration d'import sélectionnée pour le fichier CSV
  const [selectedImportId, setSelectedImportId] = useState();

  // Table qui remplit la vue, scroll interne
  const { containerRef, height: tableHeight } = useFillHeight({
    targetSelector: ".ant-table-body",
    bottomOffset: VIEW.BOTTOM_OFFSET,
  });

  // Annulation propre si on quitte la page pendant un chargement
  useEffect(() => {
    return () => abort();
  }, [abort]);

  // Chargement de la liste des produits disponibles pour le select "Lot"
  useEffect(() => {
    apiGet();
    apiGetImport();
  }, [apiGet, apiGetImport]);

  const products = useMemo(() => data?.products || [], [data]);

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.reference,
      })),
    [products]
  );

  const imports = useMemo(() => dataImport?.imports || [], [dataImport]);

  const importOptions = useMemo(
    () => imports.map((importItem) => ({
      value: importItem.id,
      label: importItem.title,
    })),
    [imports]
  );

  // Objet import complet correspondant à l'id sélectionné dans le Select
  const selectedImport = useMemo(
    () => imports.find((importItem) => importItem.id === selectedImportId),
    [imports, selectedImportId]
  );

  useEffect(() => {
    if (selectedImportId == null) {
      setBaseColumns([]);
      setDataSource([]);
    }
  }, [selectedImportId]);

  // Retrouve, pour un header de colonne CSV donné en snake_case (tel que
  // défini dans `parameters` de l'import sélectionné), le dataIndex de la
  // colonne correspondante dans baseColumns.
  const columnDataIndexBySnakeHeader = useMemo(() => {
    const map = {};
    baseColumns.forEach((c) => {
      map[toSnakeCase(c.headerLabel)] = c.dataIndex;
    });
    return map;
  }, [baseColumns]);

  // --- Auto-sélection du lot à partir de la colonne CSV désignée par
  // parameters.lot_id de l'import sélectionné (propre à chaque modèle
  // d'import) ---
  // Cherche, pour chaque ligne, le produit correspondant à la valeur de cette
  // colonne (cf. findMatchingProduct : designation ou reference, exacte puis
  // partielle, premier trouvé) et pré-remplit le select "Lot".
  // Ne touche pas aux lignes déjà sélectionnées manuellement.
  useEffect(() => {
    if (dataSource.length === 0 || products.length === 0) return;

    const lotIdSourceColumn = selectedImport?.parameters?.lot_id;
    if (!lotIdSourceColumn) return;

    const logementDataIndex = columnDataIndexBySnakeHeader[lotIdSourceColumn];
    if (!logementDataIndex) return;

    setLotSelection((prev) => {
      let hasChanges = false;
      const next = { ...prev };

      dataSource.forEach((row) => {
        if (next[row.key] !== undefined) return; // sélection déjà faite, on ne l'écrase pas

        const logementValue = row[logementDataIndex];
        const match = findMatchingProduct(logementValue, products);

        if (match) {
          next[row.key] = match.id;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [dataSource, columnDataIndexBySnakeHeader, products, selectedImport]);

  const commitImport = ({ uniqueHeaders, dataRows }) => {

    const cols = uniqueHeaders.map((header, idx) => ({
      dataIndex: `col_${idx}`,
      key: `col_${idx}`,
      headerLabel: header,
    }));

    const data = dataRows.map((r, rowIndex) => {
      const obj = { key: rowIndex };
      uniqueHeaders.forEach((_, idx) => {
        obj[`col_${idx}`] = r[idx] ?? "";
      });
      return obj;
    });

    // Toutes les colonnes sont cochées par défaut à chaque nouvel import
    const initialColumnSelection = {};
    cols.forEach((c) => {
      initialColumnSelection[c.dataIndex] = true;
    });

    setBaseColumns(cols);
    setDataSource(data);
    setSearchText(""); // on réinitialise la recherche à chaque nouvel import
    setLotSelection({}); // on réinitialise les lots choisis à chaque nouvel import
    setColumnSelection(initialColumnSelection);

    message.success(`${data.length} lignes importées`);
  };

  // --- Gestion de la sélection du lot par ligne ---
  const handleLotChange = (rowKey, value) => {
    setLotSelection((prev) => ({
      ...prev,
      [rowKey]: value,
    }));
  };

  // --- Colonne "Lot" : select par ligne, alimenté par la liste des produits ---
  const lotColumn = useMemo(
    () => ({
      title: "Lot",
      dataIndex: "lot",
      key: "lot",
      fixed: "left",
      width: 200,
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          placeholder="Sélectionner un lot"
          allowClear
          showSearch
          loading={loadingQuery}
          value={lotSelection[record.key]}
          onChange={(value) => handleLotChange(record.key, value)}
          options={productOptions}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onClick={(e) => e.stopPropagation()}
        />
      ),
    }),
    [productOptions, loadingQuery, lotSelection]
  );

  // --- Colonnes du tableau final ---
  // Toutes les colonnes CSV existent dans `columns`, mais seules celles
  // cochées (columnSelection) seront effectivement affichées dans la Table.
  const columns = React.useMemo(
    () => [
      lotColumn,
      ...baseColumns.map((c) => ({
        title: c.headerLabel,
        dataIndex: c.dataIndex,
        key: c.key,
        width: 220,
        sorter: (a, b) =>
          String(a[c.dataIndex]).localeCompare(String(b[c.dataIndex])),
      })),
    ],
    [baseColumns, lotColumn]
  );

  // Colonnes réellement affichées dans le tableau (Lot toujours visible,
  // colonnes CSV filtrées selon les cases cochées)
  const visibleColumns = React.useMemo(
    () =>
      columns.filter(
        (c) => c.key === "lot" || columnSelection[c.dataIndex] !== false
      ),
    [columns, columnSelection]
  );

  // --- Filtrage du tableau final selon le texte recherché ---
  // On ne recherche que dans les colonnes actuellement visibles
  // (celles qui ne sont pas masquées via le Select "Colonnes masquées").
  const filteredDataSource = React.useMemo(() => {
    if (!searchText.trim()) return dataSource;

    const term = searchText.trim().toLowerCase();

    const visibleDataIndexes = baseColumns
      .filter((c) => columnSelection[c.dataIndex] !== false)
      .map((c) => c.dataIndex);

    return dataSource.filter((row) =>
      visibleDataIndexes.some((dataIndex) =>
        String(row[dataIndex] ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [dataSource, searchText, baseColumns, columnSelection]);

  // --- Export JSON piloté par parameters.{champ: colonne_csv} de l'import
  // sélectionné, avec le lot_id résolu (lotSelection) pour chaque ligne ---
  // Les cases à cocher des en-têtes ne servent qu'à afficher/masquer les
  // colonnes dans le tableau : elles n'ont aucun impact sur l'export.
  const exportSelection = () => {
    // On capture les clés des lignes actuellement filtrées pour savoir quoi supprimer après l'export
    const sentKeys = filteredDataSource.map((row) => row.key);

    const parameters = selectedImport?.parameters || {};

    const jsonData = {
      receipt: generateContract,
      import_id: selectedImportId,
      items: filteredDataSource.map((row) => {
        const entry = {};
        Object.entries(parameters).forEach(([targetField, sourceColumnKey]) => {
          if (targetField === "lot_id") {
            entry.lot_id = lotSelection[row.key] ?? null;
            return;
          }
          const dataIndex = columnDataIndexBySnakeHeader[sourceColumnKey];
          entry[targetField] = dataIndex !== undefined ? row[dataIndex] : null;
        });
        return entry;
      }),
    };

    // console.log(jsonData);
    apiPost(jsonData).then((res) => {
      if (res?.status && Array.isArray(res.lots)) {
        const processedLotIds = res.lots.map((l) => l.id);

        // On supprime du tableau les lignes qui ont été envoyées ET dont le lot a été traité
        setDataSource((prev) =>
          prev.filter((row) => {
            if (!sentKeys.includes(row.key)) return true;
            return !processedLotIds.includes(lotSelection[row.key]);
          })
        );

        // On nettoie également la sélection des lots pour les lignes supprimées
        setLotSelection((prev) => {
          const next = { ...prev };
          sentKeys.forEach((key) => {
            if (processedLotIds.includes(prev[key])) {
              delete next[key];
            }
          });
          return next;
        });

        message.success(`${res.count} lots traités.`);
      }
    });
  };

  return (

    <>
      <div style={{padding:10}}>
        <Row>
          <Space direction="vertical" size={0}>
            <Title style={{margin: 0, color: '#1E2A4A'}} level={2}>Imports de données</Title>
          </Space>
        </Row>
        <Row style={{textAlign: "right", marginBottom: 20}}>
          <Col span={24} >
            <Space wrap>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Sélectionner un import"
                style={{textAlign: "left", width: 240}}
                loading={loadingQueryImport}
                value={selectedImportId}
                onChange={(value) => {
                  setSelectedImportId(value);
                  setBaseColumns([]);
                  setDataSource([]);
                }}
                options={importOptions}
              />
              <CSVImportButton
                onConfirm={commitImport}
                disabled={selectedImportId == null}
              />
            </Space>
          </Col>
        </Row>

        {dataSource.length > 0 && (
          <Space
            direction="vertical"
            style={{ width: "100%", marginTop: 16 }}
            size="small"
          >
            <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
              <Input.Search
                placeholder="Rechercher dans toutes les colonnes..."
                allowClear
                style={{ maxWidth: 400 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              <Space>
                {generateContract && (
                  <Text type="danger" strong>
                    Génération automatique des contrats active
                  </Text>
                )}
                <Button type="primary" size="small" onClick={exportSelection}>
                  Exporter les données
                </Button>
              </Space>
            </Space>

            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%", maxWidth: "100%" }}
              placeholder="Colonnes masquées"
              value={baseColumns
                .filter((c) => columnSelection[c.dataIndex] === false)
                .map((c) => c.dataIndex)}
              onChange={(hiddenValues) => {
                const next = {};
                baseColumns.forEach((c) => {
                  next[c.dataIndex] = !hiddenValues.includes(c.dataIndex);
                });
                setColumnSelection(next);
              }}
              options={baseColumns.map((c) => ({
                value: c.dataIndex,
                label: c.headerLabel,
              }))}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Space>
        )}
      </div>
      <div ref={containerRef}>
        <Table
          columns={visibleColumns}
          dataSource={filteredDataSource}
          loading={loadingQuery || loadingPost}
          className="list-data"
          pagination={false}
          scroll={{y: tableHeight ?? undefined}}
        />
      </div>
    </>
  );
}

export default ImportComponent;
